import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase, refreshSession, clearAuthData, validateAndRecoverSession } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [authError, setAuthError] = useState(null)

  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error && data) {
          setUserProfile(data)
          setAuthError(null)
        } else {
          if (error) console.error('Profile load error:', error)
          setUserProfile(null)
        }
      } catch (error) {
        console.error('Profile load error:', error)
        setUserProfile(null)
        
        if (error?.message?.includes('refresh') || error?.message?.includes('token')) {
          setAuthError('Session expired. Please log in again.');
        }
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
      setAuthError(null)
    }
  }

  const handleSessionRefresh = useCallback(async () => {
    try {
      const { data, error } = await refreshSession();
      if (error) {
        console.warn('Session refresh failed, signing out user:', error?.message);
        await signOut();
        
        if (error?.message?.includes('refresh') || 
            error?.message?.includes('token') ||
            error?.message?.includes('Invalid refresh token') ||
            error?.message?.includes('Refresh Token Not Found')) {
          setAuthError('Your session has expired. Please log in again.');
        } else {
          setAuthError('Authentication error. Please log in again.');
        }
        return false;
      }
      
      setAuthError(null);
      return true;
    } catch (error) {
      console.error('Session refresh error:', error);
      setAuthError('Authentication error. Please log in again.');
      return false;
    }
  }, []);

  const authStateHandlers = {
    onChange: async (event, session) => {
      if (event === 'TOKEN_REFRESHED' && !session) {
        setUser(null);
        profileOperations?.clear();
        setAuthError('Your session has expired. Please log in again.');
        setLoading(false);
        return;
      }

      if (event === 'TOKEN_REFRESH_FAILED') {
        setUser(null);
        profileOperations?.clear();
        setAuthError('Session expired due to token refresh failure. Please log in again.');
        setLoading(false);
        return;
      }

      if (event === 'SIGNED_OUT') {
        setUser(null);
        profileOperations?.clear();
        setAuthError(null);
        setLoading(false);
        return;
      }

      if (session?.user) {
        setUser(session?.user);
        setAuthError(null);
        profileOperations?.load(session?.user?.id);
      } else {
        setUser(null);
        profileOperations?.clear();
      }
      
      setLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;
    
    const initializeAuth = async () => {
      try {
        const validationResult = await validateAndRecoverSession();
        
        if (!mounted) return;

        if (validationResult?.cleared) {
          setUser(null);
          profileOperations?.clear();
          setAuthError('Your session was invalid and has been cleared. Please log in again.');
          setLoading(false);
          return;
        }

        if (!validationResult?.isValid) {
          setUser(null);
          profileOperations?.clear();
          setLoading(false);
          return;
        }

        const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
        
        if (!mounted) return;

        if (sessionError) {
          console.error('Error getting session after validation:', sessionError);
          
          if (sessionError?.message?.includes('refresh') || 
              sessionError?.message?.includes('token') ||
              sessionError?.message?.includes('Invalid refresh token') ||
              sessionError?.message?.includes('Refresh Token Not Found')) {
            
            await clearAuthData();
            setUser(null);
            profileOperations?.clear();
            setAuthError('Authentication tokens are invalid. Please log in again.');
          } else {
            setAuthError('Failed to initialize authentication. Please refresh the page.');
          }
          setLoading(false);
          return;
        }

        await authStateHandlers?.onChange(
          sessionData?.session ? 'INITIAL_SESSION' : null, 
          sessionData?.session
        );
        
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          if (error?.message?.includes('refresh') || 
              error?.message?.includes('token') ||
              error?.message?.includes('Invalid refresh token') ||
              error?.message?.includes('Refresh Token Not Found')) {
            
            await clearAuthData();
            setUser(null);
            profileOperations?.clear();
            setAuthError('Authentication session was corrupted. Please log in again.');
          } else {
            setAuthError('Failed to initialize authentication. Please refresh the page.');
          }
          setLoading(false);
        }
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const signIn = async (email, password) => {
    try {
      setAuthError(null);
      setLoading(true);
      
      await clearAuthData();
      
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      
      if (error) {
        if (error?.message?.includes('Invalid login credentials')) {
          setAuthError('Invalid email or password. Please try again.');
        } else if (error?.message?.includes('Email not confirmed')) {
          setAuthError('Please check your email and click the confirmation link.');
        } else if (error?.message?.includes('refresh') || 
                   error?.message?.includes('token') ||
                   error?.message?.includes('Invalid refresh token') ||
                   error?.message?.includes('Refresh Token Not Found')) {
          setAuthError('Authentication session error. Please clear your browser data and try again.');
        } else if (error?.message?.includes('Too many requests')) {
          setAuthError('Too many login attempts. Please wait a few minutes and try again.');
        } else {
          setAuthError(error?.message || 'Login failed. Please try again.');
        }
      }
      
      return { data, error }
    } catch (error) {
      console.error('Sign in error:', error);
      const errorMessage = 'Network error. Please check your connection and try again.';
      setAuthError(errorMessage);
      return { error: { message: errorMessage } }
    } finally {
      setLoading(false);
    }
  }

  const signOut = async () => {
    try {
      setAuthError(null);
      setLoading(true);
      
      // Sign out from Supabase first, then clear local storage
      try {
        await supabase?.auth?.signOut();
      } catch (signOutError) {
        console.warn('Supabase signOut warning (non-critical):', signOutError?.message);
      }

      // Clear local storage keys
      const storageKeys = [
        'idartists.admin.auth.token',
        'supabase.auth.token',
        'idartists.admin.service.token',
        'supabase.auth.admin.token',
        'sb-localhost-auth-token',
        'sb-' + window.location?.hostname?.replace(/\./g, '-') + '-auth-token',
      ];
      storageKeys?.forEach(key => {
        localStorage?.removeItem(key);
        sessionStorage?.removeItem(key);
      });
      
      setUser(null);
      profileOperations?.clear();
      
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      setUser(null);
      profileOperations?.clear();
      return { error: { message: 'Sign out completed locally. Please refresh if issues persist.' } }
    } finally {
      setLoading(false);
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      setAuthError(null);
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      
      if (!error) {
        setUserProfile(data);
      } else if (error?.message?.includes('refresh') || error?.message?.includes('token')) {
        setAuthError('Session expired. Please log in again.');
        await signOut();
      }
      
      return { data, error }
    } catch (error) {
      const errorMessage = 'Network error. Please try again.';
      setAuthError(errorMessage);
      return { error: { message: errorMessage } }
    }
  }

  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    authError,
    signIn,
    signOut,
    updateProfile,
    clearAuthError,
    refreshSession: handleSessionRefresh,
    validateSession: validateAndRecoverSession,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}